import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Loader2, MapPin, UserCheck, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const fmt = (d) => (d ? new Date(d).toLocaleDateString() : "");

function Img({ src, alt }) {
  const [err, setErr] = useState(false);
  if (err) return <div className="aspect-video grid place-items-center rounded-lg border border-[#E2E8F0] bg-[#F1F5F9] text-xs text-[#94A3B8]">Photo unavailable</div>;
  return <img src={`${API}${src}`} alt={alt} onError={() => setErr(true)}
    className="aspect-video w-full object-cover rounded-lg border border-[#E2E8F0]" />;
}

export default function PublicWitness() {
  const { slug } = useParams();
  const [state, setState] = useState("loading");
  const [data, setData] = useState(null);
  const [name, setName] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    document.title = "Witness — Property Documentation";
    axios.get(`${API}/witness/${slug}`)
      .then(({ data }) => { setData(data); setDone(!!data.attested); setState("ok"); })
      .catch(() => setState("notfound"));
  }, [slug]);

  const submit = async () => {
    if (!name.trim()) { toast.error("Please enter your name."); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/witness/${slug}/attest`, { signer_name: name.trim(), note: note.trim() || null });
      setDone(true);
      toast.success("Your statement has been recorded with this shared TenantSeal documentation.");
    } catch {
      toast.error("Couldn't record your statement — please try again.");
    } finally { setBusy(false); }
  };

  if (state === "loading") return <div className="min-h-screen bg-[#EEF2F6] p-6"><div className="max-w-3xl mx-auto space-y-4"><div className="h-10 w-48 bg-[#E2E8F0] rounded animate-pulse" /><div className="h-64 bg-[#E2E8F0] rounded-2xl animate-pulse" /></div></div>;
  if (state === "notfound") return (
    <div className="min-h-screen grid place-items-center bg-[#EEF2F6] px-6 text-center" data-testid="witness-notfound">
      <div><h1 className="font-heading text-2xl font-bold text-navy">This link is invalid or no longer available.</h1></div>
    </div>
  );

  const p = data.report.property;
  const rooms = (data.report.rooms || []).filter((r) => r.photos.length > 0);

  return (
    <div className="min-h-screen bg-[#EEF2F6]">
      <header className="border-b border-[#E2E8F0] bg-white/90 backdrop-blur-xl">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4">
          <Logo />
          <h1 className="mt-3 font-heading text-2xl font-bold text-navy">Witness — Property Documentation</h1>
          <p className="text-sm text-[#475569] flex items-center gap-1.5"><MapPin size={14} className="text-brand" /> {p.address}</p>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-6" data-testid="public-witness">
        <p className="mb-4 text-xs text-[#64748B] bg-white border border-[#E2E8F0] rounded-lg px-3 py-2">
          You've been asked to review this TenantSeal documentation and record a short personal statement.
        </p>

        {rooms.map((r, i) => (
          <div key={r.name} className="mt-4" data-testid={`witness-room-${i}`}>
            <p className="text-sm font-medium text-navy">{r.name}</p>
            <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-2">
              {r.photos.map((ph) => <Img key={ph.image_path} src={ph.image_path} alt={r.name} />)}
            </div>
          </div>
        ))}

        {/* Witness statement form */}
        {done ? (
          <div data-testid="witness-recorded" className="mt-8 rounded-2xl border border-[#A7F3D0] bg-[#ECFDF5] p-4 flex items-center gap-2.5">
            <UserCheck size={18} className="text-[#177245] shrink-0" />
            <p className="text-sm font-semibold text-[#177245]">Your statement has been recorded.</p>
          </div>
        ) : (
          <div className="mt-8 rounded-2xl border border-[#E2E8F0] bg-white p-5" data-testid="witness-form">
            <h2 className="font-heading text-lg font-semibold text-navy flex items-center gap-2"><ShieldCheck size={18} className="text-brand" /> Record your statement</h2>
            <p className="mt-1 text-sm text-[#475569]">"I viewed this TenantSeal documentation on {fmt(new Date().toISOString())}."</p>
            <input data-testid="witness-name" value={name} onChange={(e) => setName(e.target.value)} maxLength={120}
              placeholder="Your full name (required)"
              className="mt-3 w-full rounded-lg border border-[#E2E8F0] px-3 py-2.5 text-sm outline-none focus:border-brand" />
            <textarea data-testid="witness-note" value={note} onChange={(e) => setNote(e.target.value.slice(0, 500))} rows={3}
              placeholder="Add a note (optional)"
              className="mt-2 w-full rounded-lg border border-[#E2E8F0] px-3 py-2.5 text-sm outline-none focus:border-brand resize-none" />
            <div className="mt-1 flex items-center justify-between">
              <span className="text-[10px] text-[#94A3B8]">{note.length}/500</span>
              <button data-testid="witness-submit" onClick={submit} disabled={busy}
                className="inline-flex items-center gap-2 rounded-xl bg-brand hover:bg-brand-hover text-white text-sm font-semibold px-4 py-2.5 transition-colors disabled:opacity-60">
                {busy && <Loader2 size={16} className="animate-spin" />} Record statement
              </button>
            </div>
            <p className="mt-3 text-[11px] leading-relaxed text-[#94A3B8]" data-testid="witness-disclaimer">{data.disclaimer}</p>
          </div>
        )}

        <footer className="mt-10 py-6 text-center">
          <a href="/" className="text-sm text-[#94A3B8] hover:text-brand">Prepared with TenantSeal</a>
        </footer>
      </main>
    </div>
  );
}
